import React, { useContext } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import { StoresContext } from '@App/contexts'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import InnerForm from '@Fields/v2/InnerForm'
import SubmitButton from '@Fields/SubmitButton'
import { WorkModel } from '@Entities/work/work'
import { createUpdatingWorkDraft } from '@App/helpers/work-entities/creators'
import Button from '@Components/Buttons/Button'
import {
    addRecordingFormFields,
    addRecordingSchema,
    AddRecordingType,
} from '@Entities/schemas/wizardFlow/add-recording-schema'
import dayjs from 'dayjs'
import { RecordingModel } from '@Entities/work/recording-model'

interface AddRecordingSidebarProps {
    closePopupFunc(): void
}

const AddRecording = ({ closePopupFunc }: AddRecordingSidebarProps) => {
    const { workStore } = useContext(StoresContext)
    const methods = useForm<AddRecordingType>({
        mode: 'onChange',
        criteriaMode: 'all',
        resolver: zodResolver(addRecordingSchema),
        defaultValues: {
            title: '',
            artistName: '',
            isrc: '',
            duration: '',
            label: '',
        },
    })

    const addRecordingToDraft = (addRecording: AddRecordingType) => {
        const workDraftFromLocal = workStore.workDraftLocal

        if (!workDraftFromLocal) {
            return
        }
        // The existing data model uses NULL intead of UNDEFINED for some fields, so we adapt to that here, to prevent downstream issues
        const updatedWork: WorkModel = createUpdatingWorkDraft<RecordingModel>(workDraftFromLocal, 'recordings', {
            ...addRecording,
            isrc: addRecording.isrc ? addRecording.isrc : null,
            releaseDate: addRecording.releaseDate
                ? dayjs(addRecording.releaseDate).format('YYYY-MM-DDTHH:mm:ssZ')
                : null,
            duration: addRecording.duration ? addRecording.duration : null,
            label: addRecording.label ? addRecording.label : null,
        })

        workStore.setWorkDraftToLocal(updatedWork)
    }

    return (
        <div className="recording-sub-form-component">
            <h2>Add Recording</h2>
            <FormProvider {...methods}>
                <form
                    className="add-recording-form"
                    onSubmit={methods.handleSubmit((newRecording) => {
                        addRecordingToDraft(newRecording)
                        closePopupFunc()
                    })}
                >
                    <InnerForm formFields={addRecordingFormFields} />

                    <div className="add-recording-button-container">
                        <Button
                            type="secondary"
                            mainColor
                            disabled={!methods.formState.isValid}
                            onClick={async () => {
                                await methods.handleSubmit(addRecordingToDraft)()
                                methods.reset()
                            }}
                        >
                            Save & add another recording
                        </Button>
                        <SubmitButton label="Add Recording" disabled={!methods.formState.isValid} />
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default observer(AddRecording)
